import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { IEmail } from '@core/models';
import { Alert, User } from '@core/services';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { touchControls } from '@shared/utils';
import { lastValueFrom } from 'rxjs';

/**
 * ### **ForgotModalComponent**
 *
 * **selector:** ``app-forgot-modal``
 *
 * Un componente es un bloque, que contiene un template, contiene estilos y contiene la lógica, en este caso es el Componente
 * encargado de renderizar el modal que el tendra la informacion y la logica de recuperacion o restablecimiento de contraseña.
 */
@Component({
  selector: 'app-forgot-modal',
  imports: [MatDialogModule, TranslateModule, ReactiveFormsModule],
  templateUrl: './forgot-modal.html',
  styleUrl: './forgot-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ForgotModal {
  public readonly dialogRef = inject(MatDialogRef<ForgotModal>);
  private readonly fb = inject(FormBuilder);
  private readonly userService = inject(User);
  private readonly modal = inject(NgbModal);
  private readonly alertService = inject(Alert);

  /** variable que guardará el email del usuaria que va a reestablecer su contraseña*/
  private email!: IEmail;
  /** Variable que guardara la informacion del formulario */
  protected readonly formForgot = signal<FormGroup>(this.createForm());
  /** Variable que guaradar el estadode confirmación */
  private success!: boolean;
  /** Variable que guaradara el estado respuesta referente a la existencia del email */
  protected readonly mailNotExist = signal<boolean>(false);

  constructor() {}

  /**
   * Función nativa de angular que se activa una vez se empieza a cargar el componenten, en este caso
   * ejecuta la funcion createForm() e inicializa las distintas variable y objetos.
   */
  ngOnInit() {
    this.email = {
      email: '',
    };
    this.success = false;
  }

  /**
   * Función encargada de crear el formulario que cappurará el email del usuario.
   */
  private createForm(): FormGroup {
    return this.fb.group({
      email: [
        null,
        [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
        ],
      ],
    });
  }

  /**
   * Funcion que se activa una vez cierran el formulario, en caso de que los datos sean
   * invalidos el metodo se lo notificará, caso contrario llamara al metodo frogotpass() y le
   * pasará por parametro el contenido que recibio.
   * @param content Parametro que recibira un contenido del modal.
   */
  protected close(content: any) {
    const form = this.formForgot();
    if (form.invalid) {
      touchControls(form);
      return;
    }

    this.forgotpass(content);
  }

  /**
   * Función encargada de validar el email del usuario, esto con el fin de verificar
   * que hay un correo existente.
   * @param email Parametro que recibirá el email del usuario.
   */
  private async validEmail(email: string): Promise<boolean> {
    return await lastValueFrom(this.userService.validUniqueEmail(email));
  }

  /**
   * Funcion encargada de capturar el valor del email, verificarlo, reestablecer la contraseña en caso de que el email sea valido
   * y notificar al usuario de la acción realizada mediante otro modal (content).
   * @param content Parametro que contiene la referencia al modal
   */
  private async forgotpass(content: any) {
    this.email.email = this.formForgot().get('email')?.value;
    try {
      const validEmail = await this.validEmail(this.email.email);
      this.mailNotExist.set(false);
      const response = await lastValueFrom(this.userService.forgot(this.email));
      this.dialogRef.close();
      this.modal.open(content, { centered: true, windowClass: 'modal-holder' });
    } catch (e) {
      this.mailNotExist.set(true);
      this.alertService.show({
        isError: true,
        label: 'error_message_sent_email',
      });
    }
  }
}
